import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';
import { useNotification } from '../components/Notification';
import { useLanguage } from '../context/LanguageContext';
import AddressManager from '../components/AddressManager';
import { getPasswordStrength, isStrongPassword } from '../utils/passwordValidation';
import './MyAccount.css';

function MyAccount() {
  const queryClient = useQueryClient();
  const notification = useNotification();
  const { t, language } = useLanguage();
  const dateLocale = language === 'de' ? 'de-DE' : language === 'en' ? 'en-GB' : 'lt-LT';
  const [activeTab, setActiveTab] = useState('profile');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const { data: profile, isLoading } = useQuery({
    queryKey: ['user-profile'],
    queryFn: async () => {
      const res = await apiClient.get('/user/profile');
      return res.data;
    },
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (data) => {
      return apiClient.put('/user/profile', data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['user-profile']);
      notification.success(t('profileUpdated'));
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || t('profileUpdateError'));
    },
  });

  const changePasswordMutation = useMutation({
    mutationFn: async (data) => {
      return apiClient.post('/user/change-password', data);
    },
    onSuccess: () => {
      notification.success(t('passwordChanged'));
      document.getElementById('change-password-form')?.reset();
      setNewPassword('');
      setConfirmPassword('');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || t('passwordChangeError'));
    },
  });

  const sendVerificationMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post('/user/send-verification-email', { lang: language });
      return res.data;
    },
    onSuccess: (data) => {
      if (data?.alreadyVerified) {
        notification.success(t('verified'));
      } else {
        notification.success(data?.message || t('verificationEmailSent'));
      }
      queryClient.invalidateQueries(['user-profile']);
    },
    onError: (error) => {
      const msg = error.response?.data?.error || t('tryAgainLater');
      notification.error(msg);
    },
  });

  const handleProfileUpdate = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    updateProfileMutation.mutate({
      first_name: formData.get('first_name'),
      last_name: formData.get('last_name'),
      phone: formData.get('phone'),
    });
  };

  const handlePasswordChange = (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const current = formData.get('current_password');
    const newPwd = formData.get('new_password') || '';
    const confirmPwd = formData.get('confirm_password') || '';

    if (!isStrongPassword(newPwd)) {
      notification.error(t('passwordRequirements'));
      return;
    }
    if (newPwd !== confirmPwd) {
      notification.error(t('passwordMismatch'));
      return;
    }

    changePasswordMutation.mutate({
      current_password: current,
      new_password: newPwd,
      confirm_password: confirmPwd,
    });
  };

  const passwordStrength = getPasswordStrength(newPassword);
  const isPasswordValid = isStrongPassword(newPassword);

  if (isLoading) {
    return (
      <div className="my-account-page">
        <div className="my-account-container">
          <div className="loading">{t('loading')}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="my-account-page">
      <div className="my-account-container">
        <div className="account-header">
          <h1>{t('myAccountTitle')}</h1>
          <p className="account-subtitle">
            {t('accountSubtitle')}
          </p>
        </div>

        <div className="account-tabs">
          <button
            className={`account-tab ${activeTab === 'profile' ? 'active' : ''}`}
            onClick={() => setActiveTab('profile')}
          >
            {t('profile')}
          </button>
          <button
            className={`account-tab ${activeTab === 'password' ? 'active' : ''}`}
            onClick={() => setActiveTab('password')}
          >
            {t('passwordTab')}
          </button>
          <button
            className={`account-tab ${activeTab === 'security' ? 'active' : ''}`}
            onClick={() => setActiveTab('security')}
          >
            {t('security')}
          </button>
          <button
            className={`account-tab ${activeTab === 'addresses' ? 'active' : ''}`}
            onClick={() => setActiveTab('addresses')}
          >
            {t('addresses')}
          </button>
          <button
            className={`account-tab ${activeTab === 'credits' ? 'active' : ''}`}
            onClick={() => setActiveTab('credits')}
          >
            {t('creditsTab')}
          </button>
          <button
            className={`account-tab ${activeTab === 'favorites' ? 'active' : ''}`}
            onClick={() => setActiveTab('favorites')}
          >
            {t('favoritesTabShort')}
          </button>
        </div>

        <div className="account-content">
          {activeTab === 'profile' && (
            <div className="account-section-card">
              <h2 className="section-title">{t('profileInfo')}</h2>
              <form onSubmit={handleProfileUpdate} className="account-form">
                <div className="form-group">
                  <label>{t('email')}</label>
                  <input
                    type="email"
                    value={profile?.email || ''}
                    disabled
                    className="input"
                  />
                  <small>{t('emailCannotChange')}</small>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>{t('firstName')}</label>
                    <input
                      type="text"
                      name="first_name"
                      defaultValue={profile?.first_name || ''}
                      className="input"
                    />
                  </div>

                  <div className="form-group">
                    <label>{t('lastName')}</label>
                    <input
                      type="text"
                      name="last_name"
                      defaultValue={profile?.last_name || ''}
                      className="input"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>{t('phone')}</label>
                  <input
                    type="tel"
                    name="phone"
                    defaultValue={profile?.phone || ''}
                    className="input"
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={updateProfileMutation.isLoading}
                  >
                    {updateProfileMutation.isLoading ? t('saving') : t('saveChanges')}
                  </button>
                </div>
              </form>

              <div className="account-info-section">
                <h3 className="info-section-title">{t('accountInfo')}</h3>
                <div className="info-list">
                  <div className="info-item">
                    <span className="info-label">{t('accountCreated')}</span>
                    <span className="info-value">
                      {profile?.created_at ? new Date(profile.created_at).toLocaleDateString(dateLocale, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : '-'}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">{t('lastLogin')}</span>
                    <span className="info-value">
                      {profile?.last_login ? new Date(profile.last_login).toLocaleDateString(dateLocale, {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      }) : t('never')}
                    </span>
                  </div>
                  <div className="info-item">
                    <span className="info-label">{t('emailVerification')}</span>
                    <span className={`info-value ${profile?.email_verified ? 'verified' : 'not-verified'}`}>
                      {profile?.email_verified ? t('verified') : t('notVerified')}
                    </span>
                  </div>
                  {!profile?.email_verified && (
                    <div className="info-item verify-email-actions">
                      <p className="verify-email-hint">{t('verifyEmailHint')}</p>
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => sendVerificationMutation.mutate()}
                        disabled={sendVerificationMutation.isLoading}
                      >
                        {sendVerificationMutation.isLoading ? t('sending') : t('sendVerificationEmail')}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === 'password' && (
            <div className="account-section-card">
              <h2 className="section-title">{t('changePassword')}</h2>
              <form id="change-password-form" onSubmit={handlePasswordChange} className="account-form">
                <div className="form-group">
                  <label>{t('currentPassword')}</label>
                  <input
                    type="password"
                    name="current_password"
                    required
                    className="input"
                  />
                </div>

                <div className="form-group">
                  <label>{t('newPassword')}</label>
                  <input
                    type="password"
                    name="new_password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    className="input"
                  />
                  {newPassword && (
                    <div className="password-requirements" style={{ marginTop: 8, fontSize: 13 }}>
                      <div style={{ marginBottom: 4, fontWeight: 600, color: 'var(--color-gray-700)' }}>{t('passwordRequirements')}:</div>
                      <div style={{ color: passwordStrength.hasMinLength ? '#059669' : 'var(--color-gray-500)' }}>• {t('minChars')}</div>
                      <div style={{ color: passwordStrength.hasUpperCase ? '#059669' : 'var(--color-gray-500)' }}>• {t('oneUpperCase')}</div>
                      <div style={{ color: passwordStrength.hasLowerCase ? '#059669' : 'var(--color-gray-500)' }}>• {t('oneLowerCase')}</div>
                      <div style={{ color: passwordStrength.hasNumber ? '#059669' : 'var(--color-gray-500)' }}>• {t('oneNumber')}</div>
                      <div style={{ color: passwordStrength.hasSymbol ? '#059669' : 'var(--color-gray-500)' }}>• {t('oneSymbol')}</div>
                    </div>
                  )}
                </div>

                <div className="form-group">
                  <label>{t('confirmPassword')}</label>
                  <input
                    type="password"
                    name="confirm_password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="input"
                  />
                </div>

                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={changePasswordMutation.isLoading || !isPasswordValid || newPassword !== confirmPassword}
                  >
                    {changePasswordMutation.isLoading ? t('changingPassword') : t('changePassword')}
                  </button>
                </div>
              </form>
            </div>
          )}

          {activeTab === 'security' && (
            <TwoFactorAuth profile={profile} />
          )}

          {activeTab === 'addresses' && (
            <div className="account-section-card">
              <h2 className="section-title">{t('addresses')}</h2>
              <AddressManager />
            </div>
          )}

          {activeTab === 'credits' && (
            <StoreCredits />
          )}

          {activeTab === 'favorites' && (
            <MyFavoritesTab />
          )}
        </div>
      </div>
    </div>
  );
}

function MyFavoritesTab() {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const notification = useNotification();

  const { data: favorites, isLoading } = useQuery({
    queryKey: ['user-favorites'],
    queryFn: async () => {
      const res = await apiClient.get('/user/favorites');
      return res.data;
    },
  });

  const removeFavoriteMutation = useMutation({
    mutationFn: async (productId) => {
      return apiClient.delete(`/user/favorites/${productId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries(['user-favorites']);
      queryClient.invalidateQueries(['favorites-count']);
      notification.success(t('removedFromFavorites'));
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || t('errorRemovingFavorite'));
    },
  });

  if (isLoading) {
    return (
      <div className="account-section-card">
        <div className="loading">{t('loading')}</div>
      </div>
    );
  }

  if (!favorites || favorites.length === 0) {
    return (
      <div className="account-section-card">
        <h2 className="section-title">{t('favoritesTitle')}</h2>
        <div className="empty-favorites">
          <div className="empty-icon">
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
            </svg>
          </div>
          <h3>{t('emptyFavoritesText')}</h3>
          <p>{t('emptyFavoritesHint')}</p>
          <Link to="/products" className="btn btn-primary">
            {t('viewProducts')}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="account-section-card">
      <h2 className="section-title">{t('favoritesTitle')} ({favorites.length})</h2>
      <div className="favorites-grid">
        {favorites.map((favorite) => (
          <div key={favorite.id} className="favorite-card">
            <Link to={`/products/${favorite.slug}`} className="favorite-image">
              {favorite.primary_image ? (
                <img src={favorite.primary_image} alt={favorite.name} />
              ) : (
                <div className="image-placeholder">
                  <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                    <rect x="3" y="3" width="18" height="18" rx="2"></rect>
                    <path d="M9 9h6v6H9z"></path>
                  </svg>
                </div>
              )}
            </Link>
            <div className="favorite-info">
              <Link to={`/products/${favorite.slug}`}>
                <h3>{favorite.name}</h3>
              </Link>
              <div className="favorite-price">
                <span className="price">
                  {favorite.price ? parseFloat(favorite.price).toFixed(2) : '0.00'} €
                </span>
                {favorite.compare_price && parseFloat(favorite.compare_price) > parseFloat(favorite.price) && (
                  <span className="compare-price">
                    {parseFloat(favorite.compare_price).toFixed(2)} €
                  </span>
                )}
              </div>
              <button
                onClick={() => removeFavoriteMutation.mutate(favorite.product_id)}
                className="remove-favorite-btn"
                disabled={removeFavoriteMutation.isLoading}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path>
                </svg>
                {t('remove')}
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function StoreCredits() {
  const { t } = useLanguage();
  const { data: creditsData, isLoading } = useQuery({
    queryKey: ['store-credits'],
    queryFn: async () => {
      const res = await apiClient.get('/store-credits/my-credits');
      return res.data;
    },
  });

  if (isLoading) {
    return (
      <div className="account-section-card">
        <div className="loading">{t('loading')}</div>
      </div>
    );
  }

  const credits = creditsData?.credits || [];
  const totalBalance = creditsData?.total_balance || 0;

  return (
    <div className="account-section-card">
      <h2 className="section-title">{t('storeCreditsTitle')}</h2>
      
      <div className="credits-balance-card">
        <span className="balance-label">{t('totalBalance')}</span>
        <span className="balance-amount">
          {parseFloat(totalBalance).toFixed(2)} €
        </span>
      </div>

      {credits.length === 0 ? (
        <div className="empty-credits">
          <p>{t('noStoreCredits')}</p>
          <p className="credits-info">
            {t('creditsInfo')}
          </p>
        </div>
      ) : (
        <div className="credits-history">
          <h3 className="history-title">{t('creditsHistory')}</h3>
          <div className="credits-list">
            {credits.map((credit) => (
              <div key={credit.id} className="credit-item">
                <div className="credit-main">
                  <div className="credit-info">
                    <span className="credit-amount">+{parseFloat(credit.amount || 0).toFixed(2)} €</span>
                    <span className="credit-source">
                      {credit.source === 'refund' ? t('creditSourceRefund') : 
                       credit.source === 'manual' ? t('creditSourceManual') :
                       credit.source === 'promotion' ? t('creditSourcePromotion') :
                       credit.source || '-'}
                    </span>
                  </div>
                  <div className="credit-balance">
                    {t('balance')}: {parseFloat(credit.balance || 0).toFixed(2)} €
                  </div>
                </div>
                <div className="credit-details">
                  <div className="credit-detail-item">
                    <span className="credit-detail-label">{t('orderDate')}</span>
                    <span className="credit-detail-value">
                      {credit.created_at 
                        ? new Date(credit.created_at).toLocaleDateString('lt-LT')
                        : '-'}
                    </span>
                  </div>
                  {credit.expires_at && (
                    <div className="credit-detail-item">
                      <span className="credit-detail-label">{t('validUntil')}</span>
                      <span className="credit-detail-value">
                        {new Date(credit.expires_at).toLocaleDateString('lt-LT')}
                      </span>
                    </div>
                  )}
                  {credit.notes && (
                    <div className="credit-detail-item">
<span className="credit-detail-label">{t('orderNotesLabel')}</span>
                    <span className="credit-detail-value">{credit.notes}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function TwoFactorAuth({ profile }) {
  const { t } = useLanguage();
  const queryClient = useQueryClient();
  const notification = useNotification();
  const [setupStep, setSetupStep] = useState('initial');
  const [qrCode, setQrCode] = useState(null);
  const [secret, setSecret] = useState(null);
  const [backupCodes, setBackupCodes] = useState(null);
  const [verificationToken, setVerificationToken] = useState('');

  const setup2FAMutation = useMutation({
    mutationFn: async () => {
      const res = await apiClient.post('/user/2fa/setup');
      return res.data;
    },
    onSuccess: (data) => {
      setQrCode(data.qrCode);
      setSecret(data.secret);
      setSetupStep('qr');
    },
  });

  const verify2FAMutation = useMutation({
    mutationFn: async (token) => {
      const res = await apiClient.post('/user/2fa/verify', { token });
      return res.data;
    },
    onSuccess: (data) => {
      setBackupCodes(data.backupCodes);
      setSetupStep('backup-codes');
      queryClient.invalidateQueries(['user-profile']);
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || t('invalidCode'));
    },
  });

  const disable2FAMutation = useMutation({
    mutationFn: async (password) => {
      return apiClient.post('/user/2fa/disable', { password });
    },
    onSuccess: () => {
      notification.info(t('twoFADisabledSuccess'));
      queryClient.invalidateQueries(['user-profile']);
      setSetupStep('initial');
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || t('errorDisabling2FA'));
    },
  });

  const regenerateBackupCodesMutation = useMutation({
    mutationFn: async (password) => {
      const res = await apiClient.post('/user/2fa/regenerate-backup-codes', { password });
      return res.data;
    },
    onSuccess: (data) => {
      setBackupCodes(data.backupCodes);
      notification.success(t('backupCodesUpdated'));
    },
    onError: (error) => {
      notification.error(error.response?.data?.error || t('errorRegeneratingBackup'));
    },
  });

  const handleSetup = () => {
    setup2FAMutation.mutate();
  };

  const handleVerify = (e) => {
    e.preventDefault();
    verify2FAMutation.mutate(verificationToken);
  };

  const handleDisable = (e) => {
    e.preventDefault();
    const password = prompt(t('enterPasswordToDisable2FA'));
    if (password) {
      disable2FAMutation.mutate(password);
    }
  };

  const handleRegenerateBackupCodes = (e) => {
    e.preventDefault();
    const password = prompt(t('enterPasswordForBackupCodes'));
    if (password) {
      regenerateBackupCodesMutation.mutate(password);
    }
  };

  if (profile?.two_factor_enabled && setupStep === 'initial') {
    return (
      <div className="account-section-card">
        <h2 className="section-title">{t('twoFactorTitle')}</h2>
        <div className="security-status-card">
          <div className="status-badge enabled">{t('twoFactorEnabled')}</div>
          <p>{t('twoFactorProtected')}</p>
        </div>

        <div className="security-actions">
          <button
            onClick={handleDisable}
            className="btn btn-secondary"
            disabled={disable2FAMutation.isLoading}
          >
            {t('disable2FA')}
          </button>
          <button
            onClick={handleRegenerateBackupCodes}
            className="btn btn-secondary"
            disabled={regenerateBackupCodesMutation.isLoading}
          >
            {t('regenerateBackupCodes')}
          </button>
        </div>
      </div>
    );
  }

  if (setupStep === 'qr') {
    return (
      <div className="account-section-card">
        <h2 className="section-title">{t('setup2FA')}</h2>
        <div className="2fa-setup">
          <p className="setup-step">{t('twoFactorStep1')}</p>
          
          {qrCode && (
            <div className="qr-code-container">
              <img src={qrCode} alt="QR Code" className="qr-code" />
            </div>
          )}

          {secret && (
            <div className="manual-entry">
              <p>{t('orEnterManually')}</p>
              <code className="secret-code">{secret}</code>
            </div>
          )}

          <p className="setup-step">{t('twoFactorStep2')}</p>

          <form onSubmit={handleVerify} className="account-form">
            <div className="form-group">
              <input
                type="text"
                value={verificationToken}
                onChange={(e) => setVerificationToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                className="input verification-input"
                required
              />
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={verify2FAMutation.isLoading}
              >
                {verify2FAMutation.isLoading ? t('verifying') : t('save')}
              </button>
              <button
                type="button"
                onClick={() => setSetupStep('initial')}
                className="btn btn-secondary"
              >
                {t('cancel')}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  }

  if (setupStep === 'backup-codes') {
    return (
      <div className="account-section-card">
        <h2 className="section-title">{t('backupCodesTitle')}</h2>
        <div className="backup-codes-warning">
          <p><strong>{t('backupCodesWarning')}</strong></p>
        </div>

        <div className="backup-codes-grid">
          {backupCodes?.map((code, index) => (
            <div key={index} className="backup-code-item">
              {code}
            </div>
          ))}
        </div>

        <div className="form-actions">
          <button
            onClick={() => setSetupStep('initial')}
            className="btn btn-primary"
          >
            {t('done')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="account-section-card">
      <h2 className="section-title">{t('twoFactorTitle')}</h2>
      <div className="security-status-card">
        <div className="status-badge disabled">{t('twoFactorDisabled')}</div>
        <p>{t('twoFactorDisabledDesc')}</p>
      </div>

      <div className="security-info-card">
        <h3 className="info-card-title">{t('how2FAWorks')}</h3>
        <ul className="info-list">
          <li>{t('twoFAList1')}</li>
          <li>{t('twoFAList2')}</li>
          <li>{t('twoFAList3')}</li>
        </ul>
      </div>

      <div className="form-actions">
        <button
          onClick={handleSetup}
          className="btn btn-primary"
          disabled={setup2FAMutation.isLoading}
        >
          {setup2FAMutation.isLoading ? t('settingUp') : t('enable2FA')}
        </button>
      </div>
    </div>
  );
}

export default MyAccount;
